// De rekenregels van Verkennen (ronde 4), los van het tekenwerk en de pagina.
//
// Alles wat het scherm "weet" over een punt staat hier als pure functie over de
// kaart uit /api/graaf/kaart: welke punten binnen de afbakening vallen, welke
// door de filters komen, hoe een punt beweegt in de gekozen periode, hoe de
// lagen (thema's, onderwerpen) worden opgebouwd, en wat de elf snelle vragen
// opleveren. Pure functies, zodat de testkaart ze zonder canvas kan natellen.
//
// Ontwerp: docs/decisions/2026-09-03-verkennen-ronde-4-platformbouw.md en de
// gidsen Dieper graven, Van onderwerp naar bron, Door de lagen.

import type { GraafKaartResponse, GraafPunt } from "../contracts";

export type Lijn = GraafKaartResponse["lijnen"][number];

/** Maand als getal (jaar × 12 + maand) zodat periodes rekenen als getallen. */
export function maandNr(m: string | null | undefined): number | null {
  if (!m) return null;
  const [j, mm] = m.split("-").map(Number);
  if (!j || !mm) return null;
  return j * 12 + mm;
}
export function maandTekst(nr: number): string {
  const j = Math.floor((nr - 1) / 12);
  const m = ((nr - 1) % 12) + 1;
  return `${j}-${String(m).padStart(2, "0")}`;
}
const MAANDEN = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
export function maandKort(nr: number): string {
  const j = Math.floor((nr - 1) / 12);
  const m = ((nr - 1) % 12) + 1;
  return `${MAANDEN[m - 1]} ${j}`;
}

// ── banden (G6): benoemd, niet decimaal ──────────────────────────────────

export const BANDEN = {
  impact: { grenzen: [7, 8, 9], namen: ["beperkt (< 7)", "merkbaar (7–8)", "groot (8–9)", "zeer groot (9+)"] },
  urgentie: { grenzen: [7, 8, 9], namen: ["laag (< 7)", "merkbaar (7–8)", "hoog (8–9)", "zeer hoog (9+)"] },
  moeite: { grenzen: [4, 6, 8], namen: ["klein (< 4)", "gemiddeld (4–6)", "groot (6–8)", "zeer groot (8+)"] },
} as const;
export type BandAs = keyof typeof BANDEN;

export function band(v: number | null | undefined, as: BandAs): number {
  if (v == null) return -1;
  const g = BANDEN[as].grenzen;
  for (let i = 0; i < g.length; i++) if (v < g[i]!) return i;
  return g.length;
}
export function bandNaam(v: number | null | undefined, as: BandAs): string {
  const i = band(v, as);
  return i < 0 ? "onbekend" : BANDEN[as].namen[i]!.replace(/ \(.*\)/, "");
}
export function zekerheidBand(c: number | null | undefined): string {
  return c == null ? "onbekend" : c >= 0.85 ? "hoog" : c >= 0.7 ? "middel" : "laag";
}

// ── periode en beweging (K5, G9) ────────────────────────────────────────

export type Periode = { van: number; tot: number };
export type Beweging = "opkomend" | "in beweging" | "stil" | "onbekend";

/** Valt dit punt in de periode? Op de bewijsmaanden; zonder maanden op
 *  eerste/laatste bewijs; zonder datum: laten staan (het scherm zegt "geen datum"). */
export function inPeriode(p: GraafPunt, periode: Periode | null): boolean {
  if (!periode) return true;
  const mm = p.bewijsMaanden ?? [];
  if (mm.length) {
    for (const m of mm) {
      const n = maandNr(m);
      if (n !== null && n >= periode.van && n <= periode.tot) return true;
    }
    return false;
  }
  const d0 = maandNr(p.eersteBewijs);
  const d1 = maandNr(p.laatsteBewijs);
  if (d0 !== null && d1 !== null) return d0 <= periode.tot && d1 >= periode.van;
  return true;
}

/** Beweging van een punt tegen een venster: opkomend = eerste bewijs erin;
 *  in beweging = bewijs erin én ervoor; stil = alleen ervoor. */
export function beweging(p: GraafPunt, venster: Periode): Beweging {
  let mm = (p.bewijsMaanden ?? []).map(maandNr).filter((n): n is number => n !== null);
  if (!mm.length) {
    const d0 = maandNr(p.eersteBewijs);
    const d1 = maandNr(p.laatsteBewijs);
    if (d0 === null) return "onbekend";
    mm = d1 === null ? [d0] : [d0, d1];
  }
  let erin = 0;
  let ervoor = 0;
  for (const n of mm) {
    if (n >= venster.van && n <= venster.tot) erin++;
    else if (n < venster.van) ervoor++;
  }
  if (erin && !ervoor) return "opkomend";
  if (erin && ervoor) return "in beweging";
  if (!erin && ervoor) return "stil";
  return "onbekend";
}

/** Het venster waarop beweging rekent: de gekozen periode, of de laatste twaalf
 *  maanden van de data. */
export function venster(periode: Periode | null, nu: number): Periode {
  return periode ?? { van: nu - 11, tot: nu };
}

/** Het vergelijkvenster: even lang, direct vóór de periode (C2), tenzij
 *  verschoven. */
export function vergelijkVenster(periode: Periode, verschuiving = 0): Periode {
  const lengte = periode.tot - periode.van + 1;
  return { van: periode.van - lengte + verschuiving, tot: periode.van - 1 + verschuiving };
}

// ── de opbouw van de kaart: leden, thema's, buren ──────────────────────

export type Opbouw = {
  punten: GraafPunt[];
  index: Map<string, number>;
  /** onderwerp-id -> leden (indexen van trends en vaardigheden) */
  leden: Map<string, number[]>;
  /** punt-index -> onderwerp-ids */
  onderwerpenVan: Map<number, string[]>;
  /** onderwerp-id -> thema-naam ("Overig" als hij nergens staat) */
  themaVan: Map<string, string>;
  themas: { naam: string; onderwerpen: string[] }[];
  /** per punt: [buurindex, lijnindex] */
  buren: Map<number, [number, number][]>;
  /** nu: de nieuwste bewijsmaand op de kaart */
  nu: number;
  min: number;
};

export const OVERIG = "Overig";

export function bouwOp(data: GraafKaartResponse): Opbouw {
  const punten = data.punten;
  const index = new Map<string, number>();
  punten.forEach((p, i) => index.set(p.id, i));
  const leden = new Map<string, number[]>();
  const onderwerpenVan = new Map<number, string[]>();
  const buren = new Map<number, [number, number][]>();
  data.lijnen.forEach((l, li) => {
    if (l.soort === "onderwerp") {
      const ow = punten[l.naar];
      const lid = l.van;
      if (!ow) return;
      const lijst = leden.get(ow.id) ?? [];
      lijst.push(lid);
      leden.set(ow.id, lijst);
      const ovs = onderwerpenVan.get(lid) ?? [];
      ovs.push(ow.id);
      onderwerpenVan.set(lid, ovs);
      return;
    }
    const a = buren.get(l.van) ?? [];
    a.push([l.naar, li]);
    buren.set(l.van, a);
    const b = buren.get(l.naar) ?? [];
    b.push([l.van, li]);
    buren.set(l.naar, b);
  });
  const themaVan = new Map<string, string>();
  const naamNaarId = new Map<string, string>();
  for (const p of punten) if (p.soort === "onderwerp") naamNaarId.set(p.naam, p.id);
  const themas: { naam: string; onderwerpen: string[] }[] = [];
  for (const t of data.themas ?? []) {
    const ids: string[] = [];
    for (const naam of t.onderwerpen) {
      const id = naamNaarId.get(naam);
      if (id) {
        themaVan.set(id, t.naam);
        ids.push(id);
      }
    }
    themas.push({ naam: t.naam, onderwerpen: ids });
  }
  const overig: string[] = [];
  for (const p of punten) {
    if (p.soort === "onderwerp" && !themaVan.has(p.id)) {
      themaVan.set(p.id, OVERIG);
      overig.push(p.id);
    }
  }
  if (overig.length) themas.push({ naam: OVERIG, onderwerpen: overig });
  let nu = 0;
  let min = Infinity;
  for (const p of punten) {
    for (const m of p.bewijsMaanden ?? []) {
      const n = maandNr(m);
      if (n === null) continue;
      if (n > nu) nu = n;
      if (n < min) min = n;
    }
  }
  if (!nu) {
    for (const p of punten) {
      const n = maandNr(p.laatsteBewijs);
      if (n !== null && n > nu) nu = n;
    }
  }
  if (!Number.isFinite(min)) min = nu;
  min = Math.max(min, 2020 * 12 + 1);
  return { punten, index, leden, onderwerpenVan, themaVan, themas, buren, nu, min };
}

// ── afbakening en filters (K1) ─────────────────────────────────────────

export type Afbakening = { thema: string | null; onderwerp: string | null };

export type Filters = {
  toon: { trend: boolean; vaardigheid: boolean };
  /** per as: welke banden AAN staan (index 0-3); leeg = alles */
  banden: Record<BandAs, number[]>;
  horizons: string[];
  types: string[];
  alleenQuickWin: boolean;
  alleenDrager: boolean;
};

export function leegFilters(): Filters {
  return {
    toon: { trend: true, vaardigheid: true },
    banden: { impact: [], urgentie: [], moeite: [] },
    horizons: [],
    types: [],
    alleenQuickWin: false,
    alleenDrager: false,
  };
}

export function aantalFilters(f: Filters): number {
  return (
    (!f.toon.trend || !f.toon.vaardigheid ? 1 : 0) +
    (f.banden.impact.length ? 1 : 0) +
    (f.banden.urgentie.length ? 1 : 0) +
    (f.banden.moeite.length ? 1 : 0) +
    (f.horizons.length ? 1 : 0) +
    (f.types.length ? 1 : 0) +
    (f.alleenQuickWin ? 1 : 0) +
    (f.alleenDrager ? 1 : 0)
  );
}

/** De leden binnen de afbakening (null = alles). */
export function afbakeningLeden(ob: Opbouw, a: Afbakening): Set<number> | null {
  if (a.onderwerp) return new Set(ob.leden.get(a.onderwerp) ?? []);
  if (a.thema) {
    const s = new Set<number>();
    const t = ob.themas.find((x) => x.naam === a.thema);
    for (const ow of t?.onderwerpen ?? []) for (const i of ob.leden.get(ow) ?? []) s.add(i);
    return s;
  }
  return null;
}

/** 0 = buiten de afbakening (bestaat niet), 1 = gedimd (buiten de filters),
 *  2 = actief. Onderwerpen: 2 als ze in de afbakening vallen en actieve leden hebben. */
export function berekenStaat(
  ob: Opbouw,
  a: Afbakening,
  periode: Periode | null,
  f: Filters,
): Uint8Array {
  const staat = new Uint8Array(ob.punten.length);
  const set = afbakeningLeden(ob, a);
  for (let i = 0; i < ob.punten.length; i++) {
    const p = ob.punten[i]!;
    if (p.soort === "onderwerp") continue;
    if (set && !set.has(i)) continue;
    if (!inPeriode(p, periode)) continue;
    let ok = f.toon[p.soort];
    if (ok && f.banden.impact.length) ok = f.banden.impact.includes(band(p.impact, "impact"));
    if (ok && f.banden.urgentie.length) ok = f.banden.urgentie.includes(band(p.urgentie, "urgentie"));
    if (ok && f.banden.moeite.length && p.soort === "vaardigheid")
      ok = f.banden.moeite.includes(band(p.moeite, "moeite"));
    if (ok && f.alleenQuickWin && !p.quickWin) ok = false;
    if (ok && f.alleenDrager && p.laag !== "drager") ok = false;
    if (ok && f.horizons.length && p.horizon && !f.horizons.includes(p.horizon)) ok = false;
    if (ok && f.types.length && p.type && !f.types.includes(p.type)) ok = false;
    staat[i] = ok ? 2 : 1;
  }
  for (const [ow, lijst] of ob.leden) {
    const oi = ob.index.get(ow);
    if (oi === undefined) continue;
    const inAfb = !a.onderwerp && !a.thema
      ? true
      : a.onderwerp ? a.onderwerp === ow : ob.themaVan.get(ow) === a.thema;
    if (!inAfb) continue;
    let actief = 0;
    let inScope = 0;
    for (const i of lijst) {
      if (staat[i]) inScope++;
      if (staat[i] === 2) actief++;
    }
    staat[oi] = inScope ? (actief ? 2 : 1) : 0;
  }
  return staat;
}

// ── de lagen (A3): thema's en onderwerpen als bollen ───────────────────

export type Bol = {
  id: string;
  naam: string;
  x: number;
  y: number;
  /** actieve punten eronder */
  punten: number;
  vaardigheden: number;
  trends: number;
  /** punten met eerste bewijs in het venster */
  opkomend: number;
  /** groei tegenover het vergelijkvenster: punten met bewijs nu − toen */
  groei: number;
  thema: string;
};

export type Verbinding = { a: string; b: string; gedeeld: number };

function bolVan(ob: Opbouw, id: string, naam: string, ledenIdx: number[], staat: Uint8Array,
                bewegingVan: (i: number) => Beweging, thema: string): Bol {
  let xs = 0; let ys = 0; let n = 0; let v = 0; let t = 0; let op = 0; let groei = 0;
  for (const i of ledenIdx) {
    if (staat[i] !== 2) continue;
    const p = ob.punten[i]!;
    xs += p.x; ys += p.y; n++;
    if (p.soort === "vaardigheid") v++; else t++;
    const b = bewegingVan(i);
    if (b === "opkomend") { op++; groei++; }
    else if (b === "stil") groei--;
  }
  const alle = ledenIdx.length ? ledenIdx : [];
  if (!n && alle.length) {
    for (const i of alle) { xs += ob.punten[i]!.x; ys += ob.punten[i]!.y; }
    xs /= alle.length; ys /= alle.length;
  } else if (n) { xs /= n; ys /= n; }
  else { xs = 0.5; ys = 0.5; }
  return { id, naam, x: xs, y: ys, punten: n, vaardigheden: v, trends: t, opkomend: op, groei, thema };
}

/** Laag 1: de thema's als bollen; laag 2: de onderwerpen van één thema. */
export function bollen(
  ob: Opbouw,
  staat: Uint8Array,
  bewegingVan: (i: number) => Beweging,
  thema: string | null,
): { bollen: Bol[]; verbindingen: Verbinding[] } {
  const ledenPer = new Map<string, number[]>();
  const uit: Bol[] = [];
  if (thema === null) {
    for (const t of ob.themas) {
      const s = new Set<number>();
      for (const ow of t.onderwerpen) for (const i of ob.leden.get(ow) ?? []) s.add(i);
      const lijst = [...s];
      ledenPer.set(t.naam, lijst);
      uit.push(bolVan(ob, t.naam, t.naam, lijst, staat, bewegingVan, t.naam));
    }
  } else {
    const t = ob.themas.find((x) => x.naam === thema);
    for (const ow of t?.onderwerpen ?? []) {
      const p = ob.punten[ob.index.get(ow) ?? -1];
      if (!p) continue;
      const lijst = ob.leden.get(ow) ?? [];
      ledenPer.set(ow, lijst);
      uit.push(bolVan(ob, ow, p.naam, lijst, staat, bewegingVan, thema));
    }
  }
  // verbindingen = gedeelde vaardigheden (actief) tussen twee bollen
  const vaardigheden = new Map<string, Set<number>>();
  for (const [id, lijst] of ledenPer) {
    const s = new Set<number>();
    for (const i of lijst) if (staat[i] === 2 && ob.punten[i]!.soort === "vaardigheid") s.add(i);
    vaardigheden.set(id, s);
  }
  const verbindingen: Verbinding[] = [];
  const ids = [...ledenPer.keys()];
  for (let a = 0; a < ids.length; a++) {
    for (let b = a + 1; b < ids.length; b++) {
      const sa = vaardigheden.get(ids[a]!)!;
      const sb = vaardigheden.get(ids[b]!)!;
      let n = 0;
      for (const i of sa) if (sb.has(i)) n++;
      if (n) verbindingen.push({ a: ids[a]!, b: ids[b]!, gedeeld: n });
    }
  }
  verbindingen.sort((x, y) => y.gedeeld - x.gedeeld);
  return { bollen: uit.filter((b) => b.punten > 0 || thema !== null), verbindingen };
}

// ── de buren van één punt, per soort (G4) ──────────────────────────────

export type BuurSoort = "boven" | "vraagt" | "gevraagd" | "onder" | "overlapt" | "verwant";
export type Buur = { i: number; lijn: number; soort: BuurSoort; sterkte: number; zekerheid: number | null; buiten: boolean; opkomend?: boolean };

export const BUUR_LABEL: Record<BuurSoort, { naam: string; waar: string }> = {
  boven: { naam: "Breder", waar: "boven" },
  vraagt: { naam: "Vraagt om", waar: "vaardigheden" },
  gevraagd: { naam: "Gevraagd door", waar: "trends" },
  onder: { naam: "Smaller", waar: "onder" },
  overlapt: { naam: "Overlapt", waar: "breder of smaller, AI-oordeel" },
  verwant: { naam: "Verwant", waar: "AI-vermoeden" },
};
const BUUR_ORDE: BuurSoort[] = ["boven", "vraagt", "gevraagd", "onder", "overlapt", "verwant"];

export function burenVan(ob: Opbouw, lijnen: readonly Lijn[], i: number, staat: Uint8Array, bewegingVan?: (i: number) => Beweging): Buur[] {
  const p = ob.punten[i]!;
  const uit: Buur[] = [];
  for (const [j, li] of ob.buren.get(i) ?? []) {
    const l = lijnen[li]!;
    let soort: BuurSoort;
    if (l.soort === "bron") soort = p.soort === "trend" ? "vraagt" : "gevraagd";
    else if (l.soort === "ouder_kind") soort = l.naar === i ? "boven" : "onder";
    else if (l.soort === "breder_smaller") soort = "overlapt";
    else if (l.soort === "verwant") soort = "verwant";
    else continue;
    uit.push({
      i: j, lijn: li, soort, sterkte: l.sterkte ?? 1, zekerheid: l.zekerheid ?? null,
      buiten: staat[j] !== 2,
      opkomend: bewegingVan ? bewegingVan(j) === "opkomend" : undefined,
    });
  }
  uit.sort((a, b) =>
    BUUR_ORDE.indexOf(a.soort) - BUUR_ORDE.indexOf(b.soort) ||
    Number(a.buiten) - Number(b.buiten) ||
    (b.sterkte + (b.zekerheid ?? 0)) - (a.sterkte + (a.zekerheid ?? 0)) ||
    (ob.punten[b.i]!.bronnen ?? 0) - (ob.punten[a.i]!.bronnen ?? 0),
  );
  return uit;
}

// ── de snelle vragen (E3): elf tellingen, elk een lijst ────────────────

export type SnelleVraag = {
  nr: number;
  groep: "Vinden" | "Bewegen" | "Onderbouwen";
  naam: string;
  instructie: string;
  lees: string;
  /** null = alleen zinvol binnen één onderwerp (laag 3) */
  alleenInOnderwerp?: boolean;
};

export const SNELLE_VRAGEN: SnelleVraag[] = [
  { nr: 1, groep: "Vinden", naam: "Vaardigheden die overal terugkomen",
    instructie: "Vaardigheden die vanuit drie of meer onderwerpen gevraagd worden.",
    lees: "Hoe hoger in de lijst, hoe meer onderwerpen om deze vaardigheid vragen: dat is basiscurriculum." },
  { nr: 2, groep: "Vinden", naam: "De hefboom in dit onderwerp", alleenInOnderwerp: true,
    instructie: "Kies eerst een onderwerp. Dan: de vaardigheid die de meeste trends van dat onderwerp dekt.",
    lees: "Eén vaardigheid die de helft van de trends afdekt is de keuze als je maar één ding kiest." },
  { nr: 3, groep: "Vinden", naam: "Trends met het grootste kielzog",
    instructie: "Trends die de meeste vaardigheden vragen.",
    lees: "Eén ontwikkeling, twaalf gevolgen voor het onderwijs. Let op het aantal bronnen ernaast." },
  { nr: 11, groep: "Vinden", naam: "Hoge impact, weinig moeite",
    instructie: "Vaardigheden met impact groot of zeer groot en moeite klein of gemiddeld.",
    lees: "De quick wins: waar je morgen mee kunt beginnen." },
  { nr: 10, groep: "Vinden", naam: "Twee zware onderwerpen zonder verband",
    instructie: "Paren onderwerpen met veel punten en geen gedeelde vaardigheid.",
    lees: "Óf echt los van elkaar, óf een opleiding die nog niet bestaat. Stel de brugvraag aan de chat." },
  { nr: 4, groep: "Bewegen", naam: "Onderwerpen die naar elkaar toe groeien",
    instructie: "Paren onderwerpen die in de periode meer vaardigheden zijn gaan delen.",
    lees: "Waar twee onderwerpen samenkomen, ontstaat een nieuw vakgebied." },
  { nr: 5, groep: "Bewegen", naam: "Groei en krimp per onderwerp",
    instructie: "Per onderwerp: punten met bewijs in de periode tegenover de periode ervoor.",
    lees: "Niet het grootste onderwerp is interessant, maar waar het bewijs versnelt of opdroogt." },
  { nr: 6, groep: "Bewegen", naam: "Nieuw, dun en zwaar",
    instructie: "Opkomend in de periode, hoogstens twee bronnen, impact groot of zeer groot.",
    lees: "De lijst om over een half jaar opnieuw te bekijken: wat hiervan is dan bevestigd?" },
  { nr: 7, groep: "Bewegen", naam: "Veel bronnen, stilgevallen",
    instructie: "Vier of meer bronnen, maar geen bewijs meer in de periode.",
    lees: "Uitgewerkt, of een gat in onze bronnen. Beide antwoorden zijn nuttig." },
  { nr: 8, groep: "Onderbouwen", naam: "Door meer dan één bronsoort gedragen",
    instructie: "Punten waarvan de bronnen uit meerdere soorten komen (gemengd).",
    lees: "Beleid, onderzoek én bedrijfsleven die hetzelfde zeggen is een andere waarheid dan beleid alleen." },
  { nr: 9, groep: "Onderbouwen", naam: "Bevestigd door arbeidsmarktcijfers",
    instructie: "Punten die een bron delen met een arbeidsmarktobservatie (dekking 10%).",
    lees: "Harder dan een trend zonder cijfer. Het percentage dekking staat er altijd bij." },
];

export type VraagUitkomst = {
  /** punt-indexen, in leesvolgorde */
  ids: number[];
  /** een tweede kolom per rij, bijvoorbeeld "10 onderwerpen · 22 trends" */
  toelichting: Map<number, string>;
  /** voor vraag 4, 5 en 10: paren of onderwerpen in plaats van punten */
  paren?: { a: string; b: string; nu: number; was?: number }[];
  onderwerpen?: { id: string; nu: number; was: number }[];
  /** de leesregel boven de lijst */
  regel: string;
};

/** Het antwoord op één snelle vraag, binnen afbakening ∩ filter (staat === 2). */
export function beantwoord(
  vraag: SnelleVraag,
  ob: Opbouw,
  lijnen: readonly Lijn[],
  staat: Uint8Array,
  afb: Afbakening,
  periode: Periode | null,
  arbeidsmarkt?: Set<string>,
): VraagUitkomst {
  const v = venster(periode, ob.nu);
  const bew = (i: number) => beweging(ob.punten[i]!, v);
  const actief = (i: number) => staat[i] === 2;
  const toelichting = new Map<number, string>();
  const ids: number[] = [];
  const P = ob.punten;
  const vraagtOm = (t: number): number[] => (ob.buren.get(t) ?? [])
    .filter(([j, li]) => lijnen[li]!.soort === "bron" && P[j]!.soort === "vaardigheid")
    .map(([j]) => j);
  const gevraagdDoor = (s: number): number[] => (ob.buren.get(s) ?? [])
    .filter(([j, li]) => lijnen[li]!.soort === "bron" && P[j]!.soort === "trend")
    .map(([j]) => j);
  const vasteOnderwerpen = (i: number) => (ob.onderwerpenVan.get(i) ?? []);

  switch (vraag.nr) {
    case 1: {
      const scores: [number, number, number][] = [];
      for (let i = 0; i < P.length; i++) {
        if (!actief(i) || P[i]!.soort !== "vaardigheid") continue;
        const trs = gevraagdDoor(i);
        const ows = new Set<string>();
        for (const t of trs) for (const o of vasteOnderwerpen(t)) ows.add(o);
        if (ows.size >= 3) scores.push([i, ows.size, trs.length]);
      }
      scores.sort((a, b) => b[1] - a[1] || b[2] - a[2]);
      for (const [i, n, t] of scores) { ids.push(i); toelichting.set(i, `${n} onderwerpen · ${t} trends`); }
      return { ids, toelichting, regel: `${ids.length} vaardigheden worden vanuit drie of meer onderwerpen gevraagd` };
    }
    case 2: {
      if (!afb.onderwerp) return { ids: [], toelichting, regel: "Kies eerst een onderwerp." };
      const trs = new Set((ob.leden.get(afb.onderwerp) ?? []).filter((i) => actief(i) && P[i]!.soort === "trend"));
      const tel = new Map<number, number>();
      for (const t of trs) for (const s of vraagtOm(t)) tel.set(s, (tel.get(s) ?? 0) + 1);
      const rij = [...tel.entries()].sort((a, b) => b[1] - a[1]);
      for (const [i, n] of rij) { ids.push(i); toelichting.set(i, `dekt ${n} van ${trs.size} trends`); }
      return { ids, toelichting, regel: `${ids.length} vaardigheden dekken samen de ${trs.size} trends van dit onderwerp` };
    }
    case 3: {
      const rij: [number, number][] = [];
      for (let i = 0; i < P.length; i++) {
        if (!actief(i) || P[i]!.soort !== "trend") continue;
        const n = vraagtOm(i).length;
        if (n >= 3) rij.push([i, n]);
      }
      rij.sort((a, b) => b[1] - a[1] || (P[b[0]]!.bronnen ?? 0) - (P[a[0]]!.bronnen ?? 0));
      for (const [i, n] of rij) { ids.push(i); toelichting.set(i, `${n} vaardigheden · ${P[i]!.bronnen} bronnen`); }
      return { ids, toelichting, regel: `${ids.length} trends vragen drie of meer vaardigheden` };
    }
    case 11: {
      for (let i = 0; i < P.length; i++) {
        const p = P[i]!;
        if (!actief(i) || p.soort !== "vaardigheid") continue;
        if ((p.impact ?? 0) >= 8 && p.moeite != null && p.moeite < 6) { ids.push(i); toelichting.set(i, `impact ${p.impact} · moeite ${p.moeite}`); }
      }
      ids.sort((a, b) => (P[b]!.impact ?? 0) - (P[a]!.impact ?? 0) || (P[a]!.moeite ?? 0) - (P[b]!.moeite ?? 0));
      return { ids, toelichting, regel: `${ids.length} vaardigheden met grote impact en klein-tot-gemiddelde moeite` };
    }
    case 10: {
      const ows = [...ob.leden.keys()].filter((ow) => (ob.leden.get(ow) ?? []).filter(actief).length >= 25);
      const vaard = new Map<string, Set<number>>();
      for (const ow of ows) vaard.set(ow, new Set((ob.leden.get(ow) ?? []).filter((i) => actief(i) && P[i]!.soort === "vaardigheid")));
      const paren: { a: string; b: string; nu: number }[] = [];
      for (let x = 0; x < ows.length; x++) for (let y = x + 1; y < ows.length; y++) {
        const a = ows[x]!; const b = ows[y]!;
        if (ob.themaVan.get(a) === ob.themaVan.get(b)) continue;
        let n = 0;
        for (const i of vaard.get(a)!) if (vaard.get(b)!.has(i)) n++;
        if (n === 0) paren.push({ a, b, nu: 0 });
      }
      paren.sort((p, q) => ((ob.leden.get(q.a)?.length ?? 0) + (ob.leden.get(q.b)?.length ?? 0)) - ((ob.leden.get(p.a)?.length ?? 0) + (ob.leden.get(p.b)?.length ?? 0)));
      const gehad = new Set<string>();
      const top = paren.filter((p) => { if (gehad.has(p.a) || gehad.has(p.b)) return false; gehad.add(p.a); gehad.add(p.b); return true; }).slice(0, 6);
      return { ids: [], toelichting, paren: top, regel: `${top.length} paren zware onderwerpen zonder één gedeelde vaardigheid` };
    }
    case 4: {
      const was = vergelijkVenster(v);
      const ows = [...ob.leden.keys()];
      const sk = (ow: string, tot: number) => {
        const s = new Set<number>();
        for (const t of ob.leden.get(ow) ?? []) {
          if (P[t]!.soort !== "trend" || staat[t] === 0) continue;
          const d0 = (P[t]!.bewijsMaanden ?? []).map(maandNr).filter((n): n is number => n !== null);
          const eerste = d0.length ? Math.min(...d0) : maandNr(P[t]!.eersteBewijs);
          if (eerste === null || eerste > tot) continue;
          for (const s2 of vraagtOm(t)) s.add(s2);
        }
        return s;
      };
      const paren: { a: string; b: string; nu: number; was: number }[] = [];
      for (let x = 0; x < ows.length; x++) for (let y = x + 1; y < ows.length; y++) {
        const a = ows[x]!; const b = ows[y]!;
        const na = sk(a, v.tot); const nb = sk(b, v.tot);
        let nu = 0; for (const i of na) if (nb.has(i)) nu++;
        if (nu < 3) continue;
        const wa = sk(a, was.tot); const wb = sk(b, was.tot);
        let w = 0; for (const i of wa) if (wb.has(i)) w++;
        if (nu - w >= 3) paren.push({ a, b, nu, was: w });
      }
      paren.sort((p, q) => (q.nu - q.was) - (p.nu - p.was));
      return { ids: [], toelichting, paren: paren.slice(0, 12), regel: `${paren.length} paren onderwerpen kregen drie of meer vaardigheden gemeen sinds ${maandKort(was.tot)}` };
    }
    case 5: {
      const was = vergelijkVenster(v);
      const rij: { id: string; nu: number; was: number }[] = [];
      for (const [ow, lijst] of ob.leden) {
        let n = 0; let w = 0;
        for (const i of lijst) {
          if (staat[i] === 0) continue;
          const mm = (P[i]!.bewijsMaanden ?? []).map(maandNr).filter((x): x is number => x !== null);
          if (mm.some((m) => m >= v.van && m <= v.tot)) n++;
          if (mm.some((m) => m >= was.van && m <= was.tot)) w++;
        }
        if (n || w) rij.push({ id: ow, nu: n, was: w });
      }
      rij.sort((a, b) => (b.nu - b.was) - (a.nu - a.was));
      return { ids: [], toelichting, onderwerpen: rij, regel: `Per onderwerp: punten met bewijs ${maandKort(v.van)} – ${maandKort(v.tot)} tegenover ${maandKort(was.van)} – ${maandKort(was.tot)}` };
    }
    case 6: {
      for (let i = 0; i < P.length; i++) {
        const p = P[i]!;
        if (!actief(i) || p.soort === "onderwerp") continue;
        if (bew(i) === "opkomend" && (p.bronnen ?? 0) <= 2 && (p.impact ?? 0) >= 8) { ids.push(i); toelichting.set(i, `impact ${p.impact} · ${p.bronnen} bron${p.bronnen === 1 ? "" : "nen"}`); }
      }
      ids.sort((a, b) => (P[b]!.impact ?? 0) - (P[a]!.impact ?? 0));
      return { ids, toelichting, regel: `${ids.length} zwakke signalen: opkomend in de periode, hoogstens twee bronnen, impact groot of zeer groot` };
    }
    case 7: {
      for (let i = 0; i < P.length; i++) {
        const p = P[i]!;
        if (staat[i] === 0 && !(afbakeningLeden(ob, afb)?.has(i) ?? true)) continue;
        if (p.soort === "onderwerp") continue;
        if ((p.bronnen ?? 0) >= 4 && bew(i) === "stil") { ids.push(i); toelichting.set(i, `${p.bronnen} bronnen · laatste ${p.laatsteBewijs ?? "?"}`); }
      }
      ids.sort((a, b) => (P[b]!.bronnen ?? 0) - (P[a]!.bronnen ?? 0));
      return { ids, toelichting, regel: `${ids.length} punten met vier of meer bronnen en geen bewijs in ${maandKort(v.van)} – ${maandKort(v.tot)}` };
    }
    case 8: {
      for (let i = 0; i < P.length; i++) {
        const p = P[i]!;
        if (!actief(i) || p.soort === "onderwerp") continue;
        if (p.uitgever === "gemengd") { ids.push(i); toelichting.set(i, `${p.bronnen} bronnen · meerdere soorten`); }
      }
      ids.sort((a, b) => (P[b]!.bronnen ?? 0) - (P[a]!.bronnen ?? 0));
      return { ids, toelichting, regel: `${ids.length} punten worden door meer dan één bronsoort gedragen` };
    }
    case 9: {
      if (!arbeidsmarkt) return { ids: [], toelichting, regel: "Arbeidsmarktkoppeling nog niet geladen." };
      for (let i = 0; i < P.length; i++) {
        if (!actief(i) || P[i]!.soort === "onderwerp") continue;
        if (arbeidsmarkt.has(P[i]!.id)) { ids.push(i); toelichting.set(i, "arbeidsmarktcijfer uit dezelfde bron"); }
      }
      return { ids, toelichting, regel: `${ids.length} punten delen een bron met een arbeidsmarktobservatie (dekking ~10%)` };
    }
    default:
      return { ids: [], toelichting, regel: "" };
  }
}
